import { useState, useEffect } from 'react'
import { MapPin, Briefcase, Clock, Utensils, Cake, Loader } from 'lucide-react'
import { Link } from 'react-router-dom'
import { getCareers } from '../../lib/publicData'
import useInView from '../../hooks/useInView'

function ScrollReveal({ children, delay = 0, className = '' }) {
  const [ref, inView] = useInView()
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: inView ? 1 : 0,
        transform: inView ? 'translateY(0)' : 'translateY(30px)',
        transition: `opacity 0.7s ease-out ${delay}s, transform 0.7s ease-out ${delay}s`,
      }}
    >
      {children}
    </div>
  )
}

export default function Careers() {
  const [jobs, setJobs] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    getCareers().then(data => {
      setJobs(data)
      setLoading(false)
    })
  }, [])

  const limpopoJobs = jobs.filter(j => j.color === 'terra')
  const pretoriaJobs = jobs.filter(j => j.color === 'honey')

  return (
    <div>
      {/* Page header */}
      <section className="bg-charcoal relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-px" style={{ background: 'linear-gradient(90deg, transparent, #E8761A, transparent)' }} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-20 sm:py-28">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-6 h-0.5 bg-terracotta" />
            <span className="font-body text-[11px] font-semibold tracking-[0.25em] uppercase text-terracotta">Join Us</span>
          </div>
          <h1 className="font-display text-cream tracking-[0.04em] leading-[0.95]" style={{ fontSize: 'clamp(48px, 6vw, 88px)' }}>
            Careers
          </h1>
          <p className="text-sm text-muted mt-4 max-w-xl">
            Join the Desres family. We're always looking for passionate, hardworking people
            to grow with us across our two branches.
          </p>
        </div>
      </section>

      <div className="h-px" style={{ background: 'linear-gradient(90deg, transparent, #E8761A, transparent)' }} />

      {loading ? (
        <div className="flex justify-center py-20 bg-umber">
          <Loader size={28} className="animate-spin text-muted" />
        </div>
      ) : jobs.length === 0 ? (
        <section className="bg-umber">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-10 py-20 sm:py-28 text-center">
            <Briefcase size={40} className="text-muted mx-auto mb-4" />
            <h2 className="font-display text-cream text-2xl tracking-wide mb-3">No Openings Right Now</h2>
            <p className="text-sm text-muted max-w-md mx-auto">
              We don't have any vacancies at the moment, but check back soon or reach out to us directly.
            </p>
          </div>
        </section>
      ) : (
        <>
          {limpopoJobs.length > 0 && (
            <section className="bg-umber">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-20 sm:py-28">
                <ScrollReveal>
                  <div className="flex items-center gap-3 mb-8">
                    <div className="w-12 h-12 bg-surface-high rounded-xl flex items-center justify-center">
                      <Utensils size={22} className="text-ink-mid" />
                    </div>
                    <div>
                      <h2 className="font-display text-cream text-2xl tracking-wide">Desres Kitchen</h2>
                      <p className="font-body text-[11px] font-semibold tracking-[0.25em] uppercase text-terracotta">Limpopo, South Africa</p>
                    </div>
                  </div>
                </ScrollReveal>
                <div className="space-y-4">
                  {limpopoJobs.map((job, i) => (
                    <ScrollReveal key={job.id} delay={0.1 + i * 0.1}>
                      <JobCard job={job} />
                    </ScrollReveal>
                  ))}
                </div>
              </div>
            </section>
          )}

          {limpopoJobs.length > 0 && pretoriaJobs.length > 0 && (
            <div className="h-px" style={{ background: 'linear-gradient(90deg, transparent, #E8761A, transparent)' }} />
          )}

          {pretoriaJobs.length > 0 && (
            <section className="bg-charcoal">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-20 sm:py-28">
                <ScrollReveal>
                  <div className="flex items-center gap-3 mb-8">
                    <div className="w-12 h-12 bg-surface-high rounded-xl flex items-center justify-center">
                      <Cake size={22} className="text-ink-mid" />
                    </div>
                    <div>
                      <h2 className="font-display text-cream text-2xl tracking-wide">Yahweh's Delight</h2>
                      <p className="font-body text-[11px] font-semibold tracking-[0.25em] uppercase text-ochre">Pretoria, South Africa</p>
                    </div>
                  </div>
                </ScrollReveal>
                <div className="space-y-4">
                  {pretoriaJobs.map((job, i) => (
                    <ScrollReveal key={job.id} delay={0.1 + i * 0.1}>
                      <JobCard job={job} />
                    </ScrollReveal>
                  ))}
                </div>
              </div>
            </section>
          )}
        </>
      )}

      <div className="h-px" style={{ background: 'linear-gradient(90deg, transparent, #E8761A, transparent)' }} />

      {/* How to apply */}
      <section className="bg-umber">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-20 sm:py-28">
          <ScrollReveal>
            <div className="relative bg-surface border border-border-dark rounded-2xl shadow-[0_2px_12px_rgba(0,0,0,0.07)] overflow-hidden max-w-3xl mx-auto">
              <div className="p-10 text-center">
                <Briefcase size={28} className="text-ink-mid mx-auto mb-4" />
                <h2 className="font-display text-cream text-2xl tracking-wide mb-4">How to Apply</h2>
                <p className="text-sm text-ink-mid max-w-lg mx-auto mb-8 leading-relaxed">
                  Interested in joining the Desres family? Visit us in person at the branch you'd like to work at,
                  or send your CV and a short introduction to our email. Walk-ins are welcome — we believe in
                  meeting people face to face.
                </p>
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center gap-2 bg-terracotta text-white font-body text-[13px] font-bold tracking-[0.1em] uppercase px-9 py-4 rounded-full hover:bg-terracotta-glow hover:-translate-y-0.5 transition-all"
                >
                  <MapPin size={16} /> Find Our Branches
                </Link>
                <p className="text-xs text-muted mt-6">
                  Desres Kitchen & Confectionery is an equal opportunity employer.
                </p>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </div>
  )
}

function JobCard({ job }) {
  return (
    <div className="relative bg-surface border border-border-dark rounded-2xl shadow-[0_2px_12px_rgba(0,0,0,0.07)] hover:border-terracotta/40 transition-colors overflow-hidden">
      <div className="p-6">
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-3">
          <h3 className="font-display text-cream text-lg tracking-wide">{job.title}</h3>
          <div className="flex items-center gap-3">
            <span className="font-body text-[10px] font-semibold tracking-[0.15em] uppercase px-2 py-0.5 rounded-full bg-surface-high text-ink-mid border border-border-dark">
              {job.branch}
            </span>
            <span className="flex items-center gap-1 text-xs text-muted">
              <Clock size={10} /> {job.type}
            </span>
          </div>
        </div>

        <p className="text-sm text-ink-mid leading-relaxed mb-4">{job.description}</p>

        {job.requirements?.length > 0 && (
          <div>
            <p className="font-body text-[11px] font-semibold tracking-[0.25em] uppercase text-terracotta mb-2">Requirements</p>
            <div className="space-y-1.5">
              {job.requirements.map((req, i) => (
                <div key={i} className="flex items-start gap-2 text-sm text-ink-mid">
                  <span className="w-1.5 h-1.5 mt-1.5 shrink-0 bg-terracotta rounded-full" />
                  {req}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
